import { TextSubstitutionUtility } from "./TextSubstitutionUtility";
import { RuleExecutable } from "../Common/Execution/RuleExecutable";
import { ExpressionConfiguration } from "../Expressions/ExpressionConfiguration";
import { StringDataDefinition } from "../Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../Common/Testing/ITestingContext";
import { StateManager } from "../Common/StateManager";
import { TextMergeConfiguration } from "./TextMergeConfiguration";
import { ExpressionValueType } from "../Expressions/ExpressionValueType";
import type { AppGlueAny } from "../Common/AppGlueAny";

export class TextSubstitutionConfiguration extends RuleExecutable {
    defaultValue: string = "[!Field!]";
    private _fields: { [name: string]: ExpressionConfiguration } = {};

    private _text: string = "";

    get text(): string {
        return this._text;
    }

    set text(value: string) {
        this._text = value;

        let newFields: boolean = false;

        let fieldsInText = TextSubstitutionUtility.getFields(value);

        for (let f of fieldsInText) {
            if (!this._fields[f]) {
                this._fields[f] = new ExpressionConfiguration();
                newFields = true;
            }
        }

        if (newFields) StateManager.propertyChanged(this, "fields");

        StateManager.propertyChanged(this, "text");
    }

    getFields(): {
        name: string;
        config: ExpressionConfiguration;
        used: boolean;
    }[] {
        let output: {
            name: string;
            config: ExpressionConfiguration;
            used: boolean;
        }[] = [];

        let fieldsInText = TextSubstitutionUtility.getFields(this.text);

        for (let fieldName of Object.keys(this._fields)) {
            let used = fieldsInText.indexOf(fieldName) !== -1;
            let ex = this._fields[fieldName];
            ex.inputData = () => this.getInputDefinition();
            ex.executionResult = new StringDataDefinition();
            output.push({ name: fieldName, config: ex, used: used });
        }

        return output;
    }

    run(data: ExecutionData, testContext: ITestingContext | undefined): AppGlueAny {
        let fields = this.getFields();

        let mergeData: { fieldName: string; fieldValue: string }[] = [];

        for (let f of fields) {
            if (f.used === false) continue;

            let val = data.get(TextMergeConfiguration.DEFAULT_VALUE_DATA_NAME);
            if (f.config.rootExpression.expressionRoot.valueType !== ExpressionValueType.UNSET) {
                try {
                    val = f.config.run(data, testContext);
                } catch (e: AppGlueAny) {
                    if (e.message.indexOf("is missing") !== -1) val = "[Error: " + e.message + "]";
                }
            }

            if (data.get(TextMergeConfiguration.HIGHLIGHT_FIELDS_DATA_NAME)) {
                mergeData.push({
                    fieldName: f.name,
                    fieldValue: "<u><b>" + val + "</b></u>",
                });
            } else {
                mergeData.push({
                    fieldName: f.name,
                    fieldValue: val,
                });
            }
        }

        return TextSubstitutionUtility.replace(this._text, mergeData);
    }

    override getStorageData(): object {
        let d = super.getStorageData();

        Reflect.set(d, "text", this._text);

        let fields = this.getFields();

        let fieldData: { name: string; data: object }[] = [];

        for (let f of fields) {
            fieldData.push({ name: f.name, data: f.config.getStorageData() });
        }

        Reflect.set(d, "fields", fieldData);

        return d;
    }

    override setStorageData(data: object) {
        super.setStorageData(data);

        this._text = Reflect.get(data, "text");

        let fields = Reflect.get(data, "fields");

        for (let f of fields) {
            let ex = new ExpressionConfiguration();
            ex.setStorageData(Reflect.get(f, "data"));
            fields[Reflect.get(f, "name")] = ex;
        }
    }
}
